// src/admin/pages/UsersPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  Search,
  Shield,
  User,
  Eye
} from 'lucide-react';
import { authService } from '../../services/auth';
import { formatImageUrl } from '../../services/api';

const UserAvatar = ({ user, size = 'w-10 h-10' }) => (
  user.avatar ? (
    <img
      src={formatImageUrl(user.avatar)}
      alt={user.name}
      className={`${size} rounded-full object-cover flex-shrink-0`}
    />
  ) : (
    <div className={`${size} bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0`}>
      <span className="font-bold text-blue-600">
        {user.name.charAt(0).toUpperCase()}
      </span>
    </div>
  )
);

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editingUser, setEditingUser] = useState(null); // Para uso futuro
  const [currentUser] = useState(authService.getUser());

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'agent'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const result = await authService.getUsers();
    
    if (result.success) {
      setUsers(result.users || []);
    } else {
      alert(result.error);
    }
    setLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    const result = await authService.createUser({
      name: formData.name,
      email: formData.email,
      role: formData.role
    });

    if (result.success) {
      alert(result.message);
      setShowCreateForm(false);
      setFormData({
        name: '',
        email: '',
        role: 'agent'
      });
      loadUsers();
    } else {
      alert(result.error);
    }
  };

  const handleDeactivateUser = async (id, name) => {
    if (!window.confirm(`¿Estás seguro de desactivar a ${name}?`)) {
      return;
    }

    const result = await authService.deactivateUser(id);
    
    if (result.success) {
      alert(result.message);
      loadUsers();
    } else {
      alert(result.error);
    }
  };

  const handleReactivateUser = async (id, name) => {
    const result = await authService.reactivateUser(id);
    
    if (result.success) {
      alert(`Usuario ${name} reactivado`);
      loadUsers();
    } else {
      alert(result.error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  const getRoleBadge = (role) => {
    const roles = {
      admin: { color: 'bg-red-100 text-red-800', icon: Shield, label: 'Admin' },
      agent: { color: 'bg-blue-100 text-blue-800', icon: User, label: 'Agente' },
      viewer: { color: 'bg-gray-100 text-gray-800', icon: Eye, label: 'Visualizador' }
    };
    
    const roleConfig = roles[role] || roles.viewer;
    const Icon = roleConfig.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${roleConfig.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {roleConfig.label}
      </span>
    );
  };

  const getStatusBadge = (active, verified) => (
    !verified ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pendiente de verificación
      </span>
    ) : active ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Check className="w-3 h-3 mr-1" />
        Activo
      </span>
    ) : (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <X className="w-3 h-3 mr-1" />
        Inactivo
      </span>
    )
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administra los usuarios y permisos del sistema</p>
        </div>
        
        {currentUser?.role === 'admin' && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg flex items-center transition-colors"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Formulario de creación */}
      {showCreateForm && currentUser?.role === 'admin' && (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-bold text-gray-800">Invitar Nuevo Usuario</h2>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Le vamos a mandar un mail para que verifique la cuenta y elija su propia contraseña.
          </p>

          <form onSubmit={handleCreateUser} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Juan Pérez"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="juan@ejemplo.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rol *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                >
                  <option value="agent">Agente</option>
                  <option value="admin">Administrador</option>
                  <option value="viewer">Visualizador</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === 'admin' && 'Acceso completo al sistema'}
                  {formData.role === 'agent' && 'Puede gestionar propiedades'}
                  {formData.role === 'viewer' && 'Solo puede ver información'}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Enviar Invitación
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Búsqueda y estadísticas */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar usuarios por nombre, email o rol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span>Admin ({users.filter(u => u.role === 'admin').length})</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span>Agentes ({users.filter(u => u.role === 'agent').length})</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span>Activos ({users.filter(u => u.active).length})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas de usuarios - mobile */}
      <div className="md:hidden space-y-4">
        {filteredUsers.map((user) => (
          <div key={user.id} className={`bg-white rounded-xl shadow p-4 ${user.id === currentUser?.id ? 'ring-2 ring-blue-100' : ''}`}>
            <div className="flex items-center mb-3">
              <UserAvatar user={user} />
              <div className="ml-3 min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user.name}
                  {user.id === currentUser?.id && (
                    <span className="ml-2 text-xs text-blue-600">(Tú)</span>
                  )}
                </div>
                <div className="text-sm text-gray-500 truncate">{user.email}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {getRoleBadge(user.role)}
              {getStatusBadge(user.active, user.verified)}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Creado el {new Date(user.createdAt).toLocaleDateString('es-ES')}
              </span>
              <div className="flex space-x-3">
                {currentUser?.role === 'admin' && user.id !== currentUser?.id && (
                  <>
                    {user.active ? (
                      <button
                        onClick={() => handleDeactivateUser(user.id, user.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Desactivar usuario"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivateUser(user.id, user.name)}
                        className="text-green-600 hover:text-green-900"
                        title="Reactivar usuario"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    )}
                  </>
                )}
                {user.id === currentUser?.id && (
                  <button
                    className="text-blue-600 hover:text-blue-900"
                    title="Editar perfil"
                    onClick={() => navigate('/admin/profile')}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredUsers.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <Users className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
            </p>
          </div>
        )}
      </div>

      {/* Tabla de usuarios - desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha de creación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className={user.id === currentUser?.id ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <UserAvatar user={user} />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-blue-600">(Tú)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(user.active, user.verified)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex space-x-3">
                      {currentUser?.role === 'admin' && user.id !== currentUser?.id && (
                        <>
                          {user.active ? (
                            <button
                              onClick={() => handleDeactivateUser(user.id, user.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Desactivar usuario"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateUser(user.id, user.name)}
                              className="text-green-600 hover:text-green-900"
                              title="Reactivar usuario"
                            >
                              <Check className="w-5 h-5" />
                            </button>
                          )}
                        </>
                      )}
                      {user.id === currentUser?.id && (
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar perfil"
                          onClick={() => alert('Funcionalidad en desarrollo')}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Estado vacío */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm 
                ? `No hay usuarios que coincidan con "${searchTerm}"`
                : 'Comienza creando el primer usuario del sistema.'}
            </p>
            {currentUser?.role === 'admin' && !searchTerm && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Crear primer usuario
              </button>
            )}
          </div>
        )}

        {/* Pie de tabla */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Mostrando {filteredUsers.length} de {users.length} usuarios
              </p>
              <div className="text-sm text-gray-500">
                {currentUser?.role === 'admin' 
                  ? 'Tienes permisos de administrador' 
                  : 'Solo puedes ver usuarios'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Información de permisos */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-3">📋 Información sobre roles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Shield className="w-5 h-5 text-red-600 mr-2" />
              <h4 className="font-medium text-gray-800">Administrador</h4>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Gestión completa de usuarios</li>
              <li>• Crear/editar/eliminar propiedades</li>
              <li>• Acceso a reportes y estadísticas</li>
              <li>• Configuración del sistema</li>
            </ul>
          </div>
          
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <User className="w-5 h-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-gray-800">Agente</h4>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Crear y editar propiedades</li>
              <li>• Gestionar sus propias propiedades</li>
              <li>• Ver todas las propiedades</li>
              <li>• No puede gestionar usuarios</li>
            </ul>
          </div>
          
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Eye className="w-5 h-5 text-gray-600 mr-2" />
              <h4 className="font-medium text-gray-800">Visualizador</h4>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Solo lectura de propiedades</li>
              <li>• Ver reportes básicos</li>
              <li>• No puede hacer modificaciones</li>
              <li>• Ideal para ejecutivos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;